import type { CommunityScorePercentageType } from "@/components/community-score-wheel";
import type { CategoryType } from "@/components/game-categories";
import type { PaginationDataType } from "@/components/pagination";
import type { ReviewType } from "@/components/review-modal/types";
import type { GameKindEnum, OverallRatingEnum } from "@/pages/search/types";

interface AwardType {
  category?: {
    source?: {
      sourceName: string;
    };
  };
  displayTitle?: string;
  id: string;
}

export interface BadgeType {
  category?: CategoryType;
  description: string;
  earnedAt?: string;
  id: string;
  image?: string;
  name: string;
}

export type BadgeDataType = PaginationDataType<BadgeType>;

export interface FollowerType {
  id: string;
  imageId?: string;
  username: string;
}

export interface GameType {
  awards?: AwardType[];
  communityRatingCount?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  gameimageSet?: {
    id: string;
    imageId?: string;
  }[];
  id: string;
  isCompleted?: boolean;
  isSaved?: boolean;
  kind: GameKindEnum;
  location: {
    id: string;
    company: {
      id: string;
      name: string;
    };
    displayName?: string;
  };
  name: string;
  overallRating?: keyof typeof OverallRatingEnum;
  slug: string;
  userReview?: UserReview;
}

export interface UserReview {
  id: string;
  isContainingSpoilers: boolean;
  overallRating?: keyof typeof OverallRatingEnum;
  summary?: string;
}

export interface ReviewGameType {
  communityRatingCount?: number;
  communityScorePercentages?: CommunityScorePercentageType[];
  gameimageSet?: {
    id: string;
    imageId?: string;
  }[];
  id: string;
  isCompleted?: boolean;
  isSaved?: boolean;
  location: {
    id: string;
    company: {
      id: string;
      name: string;
    };
    displayName?: string;
  };
  name: string;
  overallRating?: keyof typeof OverallRatingEnum;
  primaryCategory: CategoryType;
  secondaryCategories: CategoryType[];
  slug: string;
  userReview?: UserReview;
}

export type PaginatedGames = PaginationDataType<GameType>;

export type PaginatedReviews = PaginationDataType<ReviewGameType>;

// biome-ignore lint/suspicious/noExplicitAny: dunno, lagacy
export type FacetsDataType = PaginationDataType<any>;

export const GameSortOptionsEnum = {
  ALPHABETICAL: "ALPHABETICAL",
  // DISTANCE: 'DISTANCE',
  COMMUNITY_SCORE: "COMMUNITY_SCORE",
  DATE_PLAYED: "DATE_PLAYED",
  MY_RATING: "MY_RATING",
  // USER_REVIEW: 'USER_REVIEW',
  USER_RATING: "USER_RATING",
  // WISHLIST: 'WISHLIST',
} as const;
export type GameSortOptionsEnum =
  (typeof GameSortOptionsEnum)[keyof typeof GameSortOptionsEnum];

export interface ApiVariablesType {
  filters?: {
    categorySlugs?: string[];
  };
}

interface BaseVariablesType extends ApiVariablesType {
  isSSR: boolean;
  isUser: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: GameSortOptionsEnum;
  username: string;
}

export interface GamesVariablesType extends BaseVariablesType {
  escapedOnly?: boolean;
  savedOnly?: boolean;
  userReview: boolean;
}

export interface ExperiencesCountFacetType {
  category: {
    id: string;
    name: string;
    slug: string;
  };
  count: number;
}

export interface UserType {
  bio?: string;
  experiencesCount: number;
  experiencesCountFacets?: ExperiencesCountFacetType[];
  facebookUrl?: string;
  firstName: string;
  followers?: FollowerType[];
  followersCount: number;
  id: string;
  imageId?: string;
  instagramUrl?: string;
  isBlocked?: boolean;
  isFollower?: boolean;
  isFollowing?: boolean;
  lastName: string;
  location: string;
  pronouns: string;
  reviewsCount?: number;
  tiktokUrl?: string;
  twitterUrl?: string;
  username: string;
  websiteUrl: string;
  wishlistedCount?: number;
}

export interface GamesDataType {
  facets: FacetsDataType;
  games: PaginatedGames;
  user: UserType;
}

export interface ReviewsDataType {
  facets: FacetsDataType;
  games: PaginatedReviews;
  review?: ReviewType;
  user: UserType;
}

export interface ReviewsVariablesType extends BaseVariablesType {
  includeReview: boolean;
  reviewId: string;
}

export interface UserDataType {
  badges?: BadgeDataType;
  user: UserType;
}

export interface UserVariablesType {
  isUser: boolean;
  username: string;
}

export interface BadgesVariablesType {
  categorySlugs?: string[];
  isUser: boolean;
  username: string;
}
